<template>
    <!-- Step 2 of "Create Universal Quote" (Android UQConfigFragment): trade → service type → job type,
         property type, preferred payment, unit (HVAC non-installation only) → manual-quote/create -->
    <div
        v-if="!addingUnit && !addingNotes"
        class="uqc w-full flex flex-col gap-[12px] px-[6px] font-roboto"
    >
        <!-- same look as the customer steps (Figma 658-931 / 658-1196) -->
        <h4 class="m-0 text-[16px] font-medium text-[#F67280]">
            Quote Details
        </h4>
        <p v-if="consumer" class="m-0 text-[14px] text-[#333333]">
            {{ consumer.first_name }} {{ consumer.last_name }}<span v-if="consumer.address"> · {{ consumer.address }}</span>
        </p>

        <div class="pt-1">
            <div class="flex items-center justify-between gap-2">
                <label for="uqTrade" class="uqcLabel">Trade Type <span class="text-[#EE5851]">*</span></label>
                <!-- Android "txt_ttl_notes": notes kept until the quote is created -->
                <button type="button" class="p-0 mb-[4px] border-0 bg-transparent text-[14px] font-medium text-[#7695FF] underline"
                    @click="addingNotes = true">
                    Add Notes<template v-if="notes.length"> ({{ notes.length }})</template>
                </button>
            </div>
            <FxSelect v-model="tradeId" :options="trades" label-key="title" value-key="id" placeholder="Select Trade Type"
                @change="onTradeChange" />
        </div>
        <div class="pt-1">
            <label for="uqService" class="uqcLabel">Service Type <span class="text-[#EE5851]">*</span></label>
            <FxSelect v-model="serviceTypeId" :options="serviceTypes" label-key="title" value-key="id"
                placeholder="Select Service Type" :disabled="!selectedTrade" @change="onServiceChange" />
        </div>
        <div class="pt-1">
            <label for="uqJobType" class="uqcLabel">Job Type <span class="text-[#EE5851]">*</span></label>
            <FxSelect v-model="jobTypeId" :options="jobTypes" label-key="title" value-key="id" placeholder="Select Job Type"
                :disabled="!selectedTrade" />
        </div>
        <div class="pt-1">
            <label for="uqProperty" class="uqcLabel">Property Type <span class="text-[#EE5851]">*</span></label>
            <FxSelect v-model="propertyTypeId" :options="propertyTypes" label-key="title" value-key="id"
                placeholder="Select Property Type" />
        </div>
        <div class="pt-1">
            <label for="uqPayment" class="uqcLabel">Preferred Payment Type <span class="text-[#EE5851]">*</span></label>
            <FxSelect v-model="paymentIndex" :options="paymentOptions" placeholder="Select Preferred Payment Type" inline />
        </div>
        <div v-if="isUnitRequired" class="pt-1">
            <label for="uqUnit" class="uqcLabel">Unit <span class="text-[#EE5851]">*</span></label>
            <FxSelect v-model="unitId" :options="unitOptions" :placeholder="unitsLoaded && !units.length ? 'No units found' : 'Select Unit'"
                :disabled="!units.length" inline />
            <!-- Android "add_unit_btn": a consumer / source without units adds one here -->
            <button v-if="unitsLoaded" type="button" class="mt-[8px] p-0 border-0 bg-transparent text-[14px] text-[#7695FF]"
                @click="addingUnit = true">
                + Add New Unit
            </button>
        </div>
    </div>
    <!-- footer: ← back to the customer · Create Quote, pinned to the bottom of the modal -->
    <div v-if="!addingUnit && !addingNotes" class="uqcFooter mt-auto flex items-center gap-[10px] px-[6px]">
        <button v-if="!quote" type="button" class="uqcBtnBack" aria-label="Back" :disabled="submitting" @click="$emit('back')">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M19 12H5m6-6-6 6 6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" /></svg>
        </button>
        <div class="flex-1 flex justify-end gap-[10px]">
            <button v-if="quote" type="button" class="uqcBtnCancel" :disabled="submitting" @click="$emit('back')">
                Cancel
            </button>
            <button type="button" class="uqcBtnNext" :disabled="submitting || !configLoaded" @click="submit">
                {{ quote ? "Update Quote" : "Create Quote" }}
            </button>
        </div>
    </div>
    <UQAddUnit v-else-if="addingUnit" :target="unitTarget" @cancel="addingUnit = false" @saved="unitAdded" />
    <UQNotesStep v-else :notes="notes" @done="list => { notes = list; addingNotes = false; }" />
</template>

<script>
import { useToast } from "vue-toastification";
import { uqApi } from "../../../services/uqApi";
import FxSelect from "../../partialComponent/FxSelect.vue";
import UQAddUnit from "./UQAddUnit.vue";
import UQNotesStep from "./UQNotesStep.vue";

const toast = useToast();

// Android StaticMethods.generateScheduleMessage
const JOB_LABELS = {
    appt: ["Appointment", "Appointments"],
    uq: ["Universal Quote", "Universal Quotes"],
    sar: ["Service & Repair", "Service & Repair"],
    mq: ["Universal Quote", "Universal Quotes"],
    rq: ["Replacement Quote", "Replacement Quotes"],
};
function scheduleMessage(items) {
    const counts = {};
    (items || []).forEach(i => {
        if (JOB_LABELS[i?.job_type]) counts[i.job_type] = (counts[i.job_type] || 0) + 1;
    });
    const lines = Object.keys(JOB_LABELS)
        .filter(k => counts[k])
        .map(k => `• ${counts[k]} ${counts[k] === 1 ? JOB_LABELS[k][0] : JOB_LABELS[k][1]}`);
    if (!lines.length) return null;
    return "You already have work scheduled during this time:<br><br>" + lines.join("<br>")
        + "<br><br>Continuing will add another job to your schedule.<br><br>Do you want to continue?";
}

export default {
    components: { FxSelect, UQAddUnit, UQNotesStep },
    props: {
        // from step 1: { consumer_id (company consumer id), address_id, first_name, last_name, email, phone, address }
        consumer: { type: Object, default: null },
        // Edit Quote of a normal quote (Android UQConfigFragment update mode): the quote whose config is changed
        quote: { type: Object, default: null },
    },
    emits: ["back", "created", "updated"],
    data() {
        return {
            configLoaded: false,
            trades: [],
            propertyTypes: [],
            paymentTypes: [],
            tradeId: null,
            serviceTypeId: null,
            jobTypeId: null,
            propertyTypeId: null,
            paymentIndex: null,
            units: [],
            unitsLoaded: false,
            unitId: null,
            addingUnit: false,
            addingNotes: false,
            notes: [],
            submitting: false,
        };
    },
    computed: {
        selectedTrade() {
            return this.trades.find(t => t.id === this.tradeId) || null;
        },
        serviceTypes() {
            return this.selectedTrade?.types ?? [];
        },
        jobTypes() {
            return this.selectedTrade?.job_types ?? [];
        },
        // the option value is the index into paymentTypes (like the old <select>)
        paymentOptions() {
            return this.paymentTypes.map((p, i) => ({ label: p.display_name, value: i }));
        },
        unitOptions() {
            return this.units.map(u => ({ label: u.unit_name || ("Unit #" + u.id), value: u.id }));
        },
        selectedServiceType() {
            return this.serviceTypes.find(s => s.id === this.serviceTypeId) || null;
        },
        // units belong to the quote source, or to the consumer's address (like units/list)
        unitTarget() {
            const c = this.consumer;
            return c?.source_type && c.source_type !== "consumer"
                ? { source_type: c.source_type, source_id: c.source_id }
                : { address_id: c?.address_id };
        },
        // Android setUnitUI: HVAC trade and a service type other than "Installation"
        isUnitRequired() {
            return !!(this.selectedTrade && (this.selectedTrade.title || "").toUpperCase().includes("HVAC")
                && this.selectedServiceType && this.selectedServiceType.title
                && this.selectedServiceType.title.toLowerCase() !== "installation");
        },
    },
    mounted() {
        this.loadConfig();
    },
    methods: {
        async loadConfig() {
            const res = await uqApi.config();
            if (!res.ok) {
                toast.error(res.message || "Unable to load quote options.");
                return;
            }
            this.trades = res.body?.trades ?? [];
            this.propertyTypes = res.body?.property_types ?? [];
            this.paymentTypes = res.body?.payment_types ?? [];
            this.configLoaded = true;
            if (this.quote) this.prefill();
        },
        // edit: start from the quote's current trade / service / job / property / payment / unit
        prefill() {
            const q = this.quote;
            this.tradeId = q.trade?.id ?? null;
            this.serviceTypeId = q.service_type?.id ?? null;
            this.jobTypeId = q.job?.id ?? null;
            this.propertyTypeId = q.property_type?.id ?? null;
            const pay = q.selected_payment || {};
            const index = this.paymentTypes.findIndex(p => p.payment_type === pay.payment_type
                && String(p.payment_sub_type ?? "") === String(pay.payment_sub_type ?? ""));
            this.paymentIndex = index >= 0 ? index : null;
            if (this.isUnitRequired) this.loadUnits().then(() => { this.unitId = q.unit?.id ?? null; });
        },
        onTradeChange() {
            this.serviceTypeId = null;
            this.jobTypeId = null;
            this.resetUnits();
        },
        onServiceChange() {
            this.resetUnits();
            if (this.isUnitRequired) this.loadUnits();
        },
        resetUnits() {
            this.units = [];
            this.unitsLoaded = false;
            this.unitId = null;
        },
        async loadUnits() {
            // like Android getUnitList: an appointment / RQ / S&R source gives its units, a consumer its address's
            const res = await uqApi.units(this.unitTarget);
            this.units = res.ok && Array.isArray(res.body) ? res.body : [];
            this.unitsLoaded = true;
            if (!res.ok) {
                toast.error(res.message || "Unable to load units.");
            } else if (!this.units.length) {
                toast.info("No units found, please add a new unit to continue.");
            }
        },
        // units/update answers with the whole unit list: the new unit is the one that wasn't there before
        unitAdded(list) {
            const before = this.units.map(u => String(u.id));
            this.units = list;
            this.unitId = list.find(u => !before.includes(String(u.id)))?.id ?? this.unitId;
            this.addingUnit = false;
        },
        // Android checkValidation
        validate() {
            const msg = !this.selectedTrade ? "Please select Trade Type."
                : !this.selectedServiceType ? "Please select Service Type."
                    : !this.jobTypeId ? "Please select Job Type."
                        : !this.propertyTypeId ? "Please select Property Type."
                            : this.paymentIndex === null ? "Please select Preferred Payment Type."
                                : (this.isUnitRequired && !this.unitId) ? "Unit field required."
                                    : null;
            if (msg) toast.error(msg);
            return !msg;
        },
        async submit() {
            if (this.submitting || !this.validate()) return;
            // Android checkTechValidate → confirm → createUQ / updateUQ
            const check = await uqApi.validateTechnician(this.quote ? { job_id: this.quote.id, visit_date: this.quote.created_at } : {});
            const count = check.ok ? (check.body?.count ?? 0) : 0;
            const message = count > 0
                ? (scheduleMessage(check.body?.items) || "You already have one or more active jobs. Do you want to create another job?")
                : this.quote ? "Are you sure you want to update this Universal Quote?" : "Are you sure you want to create this Universal Quote?";
            this.$confirmDialog.show({
                title: count > 0 ? "Confirmation" : (this.quote ? "Alert" : "Create Universal Quote"),
                text: message,
                message: count > 0 ? message : "",
                buttonText: "Yes",
                onConfirm: () => (this.quote ? this.update() : this.create()),
            });
        },
        async update() {
            const payment = this.paymentTypes[this.paymentIndex];
            const params = {
                manual_quote_id: this.quote.id,
                job_type_id: this.jobTypeId,
                trade_id: this.tradeId,
                property_type: this.propertyTypeId,
                service_type: this.serviceTypeId,
                payment_type: payment?.payment_type,
                payment_sub_type: payment?.payment_sub_type,
            };
            if (this.isUnitRequired) params.unit_id = this.unitId;
            this.submitting = true;
            const res = await uqApi.update(params);
            if (!res.ok) {
                this.submitting = false;
                toast.error((typeof res.body === "string" && res.body) || res.message || "Unable to update Universal Quote.");
                return;
            }
            // notes added here belong to the existing quote (Android posts them straight away in update mode)
            for (const note of this.notes) await uqApi.addNote(this.quote.id, note.notes);
            this.submitting = false;
            toast.success("Universal Quote Updated Successfully.");
            this.$emit("updated", res.body);
        },
        async create() {
            const payment = this.paymentTypes[this.paymentIndex];
            const params = {
                source_type: this.consumer.source_type || "consumer",
                source_id: this.consumer.source_id || 0,
                consumer_id: this.consumer.consumer_id,
                job_type_id: this.jobTypeId,
                trade_id: this.tradeId,
                property_type: this.propertyTypeId,
                service_type: this.serviceTypeId,
                payment_type: payment?.payment_type,
                payment_sub_type: payment?.payment_sub_type,
            };
            if (this.consumer.address_id) params.address_id = this.consumer.address_id;
            if (this.isUnitRequired) params.unit_id = this.unitId;
            // Android createUQ: notes as JSON [{ notes, sender_name, created_at }] (MQCreateService::resolveRequestNotes)
            if (this.notes.length) params.notes = JSON.stringify(this.notes);

            this.submitting = true;
            const res = await uqApi.create(params);
            this.submitting = false;
            if (!res.ok || !res.body?.id) {
                toast.error((typeof res.body === "string" && res.body) || res.message || "Unable to create Universal Quote.");
                return;
            }
            toast.success(res.message || "Universal Quote Created Successfully.");
            this.$emit("created", res.body);
        },
    },
};
</script>
